import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, FileText, Briefcase, ChevronRight, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatApiError } from "@/lib/api";

const STAGES = ["applied", "screening", "tech_interview", "hr_interview", "offer", "hired"];
const STAGE_LABELS = {
  applied: "Applied", screening: "Screening", tech_interview: "Tech Interview",
  hr_interview: "HR Interview", offer: "Offer", hired: "Hired",
};

const JOB_DESIGNATIONS = [
  "Medical Coder",
  "Trainee Medical Coder",
  "Quality Analyst",
  "Process Coach",
  "Team Lead",
  "Manager",
];

const DEPARTMENTS = ["Medical Coding", "Home Health Medical Coding", "Engineering", "Human Resources", "Business Development", "Finance", "IT Operations", "Sales", "Operations"];
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];

export default function Recruitment() {
  const [candidates, setCandidates] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [openCandidate, setOpenCandidate] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ offered_salary: "", joining_date: "", location: "Bengaluru" });
  const [postJobOpen, setPostJobOpen] = useState(false);
  const [postingJob, setPostingJob] = useState(false);
  const [newJob, setNewJob] = useState({
    title: "",
    department: "Operations",
    openings: 1,
    experience: "",
    salary_min: 0,
    salary_max: 0,
    location: "Bengaluru",
    employment_type: "Full-time",
    hiring_manager: "",
    status: "open",
  });

  useEffect(() => {
    api.get("/candidates").then((r) => setCandidates(r.data));
  }, [refresh]);

  const resetNewJob = () => setNewJob({
    title: "",
    department: "Operations",
    openings: 1,
    experience: "",
    salary_min: 0,
    salary_max: 0,
    location: "Bengaluru",
    employment_type: "Full-time",
    hiring_manager: "",
    status: "open",
  });

  const submitNewJob = async () => {
    if (!newJob.title) { toast.error("Please select a designation"); return; }
    if (!newJob.experience) { toast.error("Experience is required"); return; }
    if (Number(newJob.salary_max) > 0 && Number(newJob.salary_min) > Number(newJob.salary_max)) {
      toast.error("Salary min must be ≤ salary max"); return;
    }
    setPostingJob(true);
    try {
      await api.post("/jobs", {
        ...newJob,
        openings: Number(newJob.openings) || 1,
        salary_min: Number(newJob.salary_min) || 0,
        salary_max: Number(newJob.salary_max) || 0,
      });
      toast.success("Job posted");
      setPostJobOpen(false);
      resetNewJob();
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to post job");
    } finally {
      setPostingJob(false);
    }
  };

  const advance = async (c, stage) => {
    await api.post(`/candidates/${c.id}/advance`, { stage });
    toast.success(`Moved to ${STAGE_LABELS[stage]}`);
    setRefresh((v) => v + 1);
  };

  const downloadResume = async (fileId) => {
    if (!fileId) { toast.error("No resume on file"); return; }
    const token = localStorage.getItem("hr_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download?auth=${token}`, "_blank");
  };

  const generateOffer = async () => {
    if (!openCandidate) return;
    try {
      const { data } = await api.post(`/candidates/${openCandidate.id}/offer-letter`, {
        offered_salary: Number(offer.offered_salary),
        joining_date: offer.joining_date,
        location: offer.location,
        job_title: openCandidate.job_title,
      });
      toast.success("Offer letter generated");
      setOfferOpen(false);
      setRefresh((v) => v + 1);
      const token = localStorage.getItem("hr_token");
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}/download?auth=${token}`, "_blank");
    } catch (e) {
      toast.error("Failed to generate offer");
    }
  };

  return (
    <div data-testid="recruitment-page">
      <PageHeader
        title="Recruitment"
        subtitle="Job requisitions, candidate pipeline, and offer letter generation."
        action={
          <Button data-testid="post-job" onClick={() => setPostJobOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
            <Plus className="h-4 w-4 mr-1.5" /> Post job
          </Button>
        }
      />

      <Tabs defaultValue="pipeline">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Pipeline</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] rounded-none pb-3 bg-transparent">Job requisitions</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STAGES.map((s) => {
              const items = candidates.filter((c) => c.stage === s);
              return (
                <div key={s} className="bg-gray-50/70 rounded-lg p-3 min-h-[200px]">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center justify-between mb-3">
                    <span>{STAGE_LABELS[s]}</span>
                    <span className="font-mono text-gray-400">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map((c) => (
                      <Card
                        key={c.id}
                        data-testid={`candidate-card-${c.id}`}
                        className="p-3 cursor-pointer hover:shadow transition border-gray-200"
                        onClick={() => setOpenCandidate(c)}
                      >
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-[11px] text-gray-500 truncate">{c.job_title}</div>
                        <div className="text-[10px] text-gray-400 mt-1">{c.experience || "—"} · NP {c.notice_period || "—"}</div>
                        {c.source === "public_careers" && (
                          <span className="inline-block mt-1 text-[9px] uppercase tracking-wider text-[#1E88E5] font-semibold">via careers site</span>
                        )}
                      </Card>
                    ))}
                    {items.length === 0 && (
                      <div className="text-[11px] text-gray-400 italic">—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-3">Tip: click any candidate card to view details, download resume, generate offer letter, or advance stage.</p>
        </TabsContent>

        <TabsContent value="jobs">
          <DataTable
            testId="jobs-table"
            endpoint="/jobs"
            columns={[
              { key: "title", label: "Position", render: (r) => (
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-[#0F172A]" />
                  <span className="font-medium">{r.title}</span>
                </div>
              )},
              { key: "department", label: "Department" },
              { key: "openings", label: "Openings", align: "right" },
              { key: "experience", label: "Experience" },
              { key: "location", label: "Location" },
              { key: "salary_min", label: "Salary", render: (r) => `₹${(r.salary_min/100000).toFixed(1)}–${(r.salary_max/100000).toFixed(1)} LPA` },
              { key: "status", label: "Status", badge: true },
            ]}
          />
        </TabsContent>
      </Tabs>

      {/* Candidate detail dialog */}
      <Dialog open={!!openCandidate} onOpenChange={(v) => !v && setOpenCandidate(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{openCandidate?.name}</DialogTitle>
          </DialogHeader>
          {openCandidate && (
            <div className="space-y-2 text-sm">
              <div className="text-gray-500">Applied for <b className="text-gray-800">{openCandidate.job_title}</b></div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Info label="Email" value={openCandidate.email} />
                <Info label="Mobile" value={openCandidate.mobile} />
                <Info label="Experience" value={openCandidate.experience} />
                <Info label="Notice period" value={openCandidate.notice_period} />
                <Info label="Current company" value={openCandidate.current_company} />
                <Info label="Expected CTC" value={openCandidate.expected_salary ? `₹ ${Number(openCandidate.expected_salary).toLocaleString("en-IN")}` : "—"} />
              </div>
              {openCandidate.cover_letter && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mt-3">Cover note</div>
                  <div className="text-xs text-gray-700 mt-1 bg-gray-50 rounded-md p-2">{openCandidate.cover_letter}</div>
                </div>
              )}

              {openCandidate.id_proofs && Object.keys(openCandidate.id_proofs).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 mt-3">ID proofs uploaded</div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {Object.entries(openCandidate.id_proofs).map(([kind, fileId]) => (
                      <Button
                        key={kind}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => downloadResume(fileId)}
                        data-testid={`download-idproof-${kind}`}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        {(() => {
                          const labels = { aadhaar: "Aadhaar", pan: "PAN", voter_id: "Voter ID" };
                          return labels[kind] || "Passport";
                        })()}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-100 pt-3 mt-3 flex flex-wrap gap-2">
                {openCandidate.resume_file_id && (
                  <Button size="sm" variant="outline" onClick={() => downloadResume(openCandidate.resume_file_id)} data-testid="download-resume">
                    <FileText className="h-3.5 w-3.5 mr-1" /> Resume
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => advance(openCandidate, STAGES[Math.min(STAGES.indexOf(openCandidate.stage)+1, STAGES.length-1)])} data-testid="advance-candidate">
                  <ChevronRight className="h-3.5 w-3.5 mr-1" /> Advance stage
                </Button>
                {openCandidate.stage !== "hired" && (
                  <Button size="sm" className="bg-[#F26B3A] hover:bg-[#d85d2f]" onClick={() => { setOffer({ offered_salary: openCandidate.expected_salary || "", joining_date: "", location: "Bengaluru" }); setOfferOpen(true); }} data-testid="generate-offer">
                    <Mail className="h-3.5 w-3.5 mr-1" /> Generate offer
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post job dialog */}
      <Dialog open={postJobOpen} onOpenChange={(v) => { setPostJobOpen(v); if (!v) resetNewJob(); }}>
        <DialogContent className="max-w-lg" data-testid="post-job-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-[#F26B3A]" /> Post a new job</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Designation</Label>
              <Select value={newJob.title} onValueChange={(v) => setNewJob({ ...newJob, title: v })}>
                <SelectTrigger data-testid="job-title-select" className="mt-1.5">
                  <SelectValue placeholder="Select designation" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_DESIGNATIONS.map((d) => (
                    <SelectItem key={d} value={d} data-testid={`job-title-option-${d.replace(/\s+/g, "-").toLowerCase()}`}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Department</Label>
              <Select value={newJob.department} onValueChange={(v) => setNewJob({ ...newJob, department: v })}>
                <SelectTrigger data-testid="job-dept-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Employment type</Label>
              <Select value={newJob.employment_type} onValueChange={(v) => setNewJob({ ...newJob, employment_type: v })}>
                <SelectTrigger data-testid="job-emp-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Openings</Label>
              <Input data-testid="job-openings" type="number" min="1" className="mt-1.5" value={newJob.openings} onChange={(e) => setNewJob({ ...newJob, openings: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Experience</Label>
              <Input data-testid="job-experience" placeholder="e.g. 2-4 yrs" className="mt-1.5" value={newJob.experience} onChange={(e) => setNewJob({ ...newJob, experience: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Salary min (INR)</Label>
              <Input data-testid="job-salary-min" type="number" className="mt-1.5" value={newJob.salary_min} onChange={(e) => setNewJob({ ...newJob, salary_min: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Salary max (INR)</Label>
              <Input data-testid="job-salary-max" type="number" className="mt-1.5" value={newJob.salary_max} onChange={(e) => setNewJob({ ...newJob, salary_max: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Location</Label>
              <Input data-testid="job-location" className="mt-1.5" value={newJob.location} onChange={(e) => setNewJob({ ...newJob, location: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Hiring manager</Label>
              <Input data-testid="job-hiring-manager" className="mt-1.5" placeholder="e.g. Marcus Vale" value={newJob.hiring_manager} onChange={(e) => setNewJob({ ...newJob, hiring_manager: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Status</Label>
              <Select value={newJob.status} onValueChange={(v) => setNewJob({ ...newJob, status: v })}>
                <SelectTrigger data-testid="job-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open (publish to careers page)</SelectItem>
                  <SelectItem value="approval_pending">Approval pending</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="post-job-cancel" onClick={() => setPostJobOpen(false)}>Cancel</Button>
            <Button data-testid="post-job-submit" onClick={submitNewJob} disabled={postingJob} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {postingJob ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer letter dialog */}
      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate offer letter</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Annual CTC (INR)</Label>
              <Input type="number" data-testid="offer-salary" value={offer.offered_salary} onChange={(e) => setOffer({ ...offer, offered_salary: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Joining date</Label>
              <Input type="date" data-testid="offer-joining" value={offer.joining_date} onChange={(e) => setOffer({ ...offer, joining_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Location</Label>
              <Input data-testid="offer-location" value={offer.location} onChange={(e) => setOffer({ ...offer, location: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="offer-submit" onClick={generateOffer} className="bg-[#F26B3A] hover:bg-[#d85d2f]">Generate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-gray-800">{value || "—"}</div>
    </div>
  );
}
